import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule, DatePipe, NgIf } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { ErrorDialogStopComponent } from 'src/app/core/dialog/error-dialog-stop/error-dialog-stop.component';
import { AuthService } from 'src/app/core/services/auth-security/auth.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { ParkerStateService } from 'src/app/core/services/state/parker-state.service';
import { DialogConfirmSpaceEmailComponent } from 'src/app/profiles/parker-v2/parker-v2-dialogs/dialog-confirm-space-email/dialog-confirm-space-email.component';
import { IApnNewSpaceEmailLink, IParkerProfileDetail, ISpaceEmailByEntityId } from 'src/app/shared/interfaces';

@Component({
  selector: 'app-parkers-v2-space-email',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    NgIf,
    ClipboardModule,
    MatButtonModule,
    FormsModule,
    MatIconModule,
  ],
  templateUrl: './parkers-v2-space-email.component.html',
  styleUrl: './parkers-v2-space-email.component.scss',
})
export class ParkersV2SpaceEmailComponent implements OnInit {
  @Input() parkerDetail: IParkerProfileDetail;
  spaceEmailValue: string = '';
  parkerId: string;
  resendEmailMode: boolean = false;
  resendEmailForm: FormGroup;
  emailSpaceForm: FormGroup;
  isCompanyPaid: boolean = false;
  spaceEmailAddressForCopy: string = '';

  //Services
  private destroyRefService = inject(DestroyRef);
  _appGeneralState = inject(AppGeneralStateService);

  constructor(
    private dataService: DataService,
    private parkerStateService: ParkerStateService,
    private snackBarService: MatSnackBar,
    private authService: AuthService,
    private datePipe: DatePipe,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    //this.parkerDetail = this.parkerStateService.getParkerDetail();
    this.initResendEmailForm();
    this.initEmailSpaceForm();
    this.getSpaceEmailByParkerId(this.parkerDetail.parkerId);
    this.evaluateCompanyOrParkerPaidType();
  }

  onCopyMessage(message: string): void {
    this.snackBarService.open(message, '', { duration: 3000 });
  }

  evaluateCompanyOrParkerPaidType(): void {
    if (this.parkerDetail.parkerPayType === 'Company') {
      this.isCompanyPaid = true;
    } else {
      this.isCompanyPaid = false;
    }
  }

  public onKeyDown(keyDown: KeyboardEvent): void {
    if (keyDown.code == 'Space') keyDown.stopPropagation();
  }

  onSubmitResendSpaceEmail(): void {
    let emailToRequestResend: string = this.resendEmailForm.get('emailAddress')!.value;

    this.getSpaceUserApnEmailResend(emailToRequestResend);
  }

  openConfirmAddNewParkerSpaceEmailDialog(enterAnimationDuration: string, exitAnimationDuration: string): void {
    const dialogRef = this.dialog.open(DialogConfirmSpaceEmailComponent, {
      data: { emailAddress: this.emailSpaceForm.get('emailAddress')!.value },
      width: '450px',
      enterAnimationDuration,
      exitAnimationDuration,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result == true) {
        let newEmailAddress: string = this.emailSpaceForm.get('emailAddress')!.value;
        //remove tab if user pasted string with tab
        newEmailAddress = newEmailAddress.replace('\t', '');

        this.emailSpaceForm.setValue({
          emailAddress: newEmailAddress,
        });

        this.isEmailAvailableForParkerPaidSpaceAccount(newEmailAddress);
      }
    });
  }

  onSubmitAddNewSpaceEmail(): void {
    this.openConfirmAddNewParkerSpaceEmailDialog('0ms', '0ms');
  }

  finishAddNewSpaceEmail(newSpaceUserId: number): void {
    let newSpaceEmailLinkData: IApnNewSpaceEmailLink = {} as IApnNewSpaceEmailLink;
    let newEmailAddress: string = this.emailSpaceForm.get('emailAddress')!.value;

    newSpaceEmailLinkData.EmailAddress = newEmailAddress;
    newSpaceEmailLinkData.ParkerId = this.parkerDetail.parkerId;
    newSpaceEmailLinkData.LotNumber = this.parkerDetail.lotNumber;
    newSpaceEmailLinkData.SpaceUserId = newSpaceUserId;
    newSpaceEmailLinkData.LastModifiedByUserId = this._appGeneralState.userName;
    newSpaceEmailLinkData.dateLastModified = new Date().toLocaleString();

    this.postNewSpaceEmail(newSpaceEmailLinkData);
  }

  initEmailSpaceForm(): void {
    try {
      this.emailSpaceForm = new FormGroup({
        emailAddress: new FormControl('', [Validators.required, Validators.email]),
      });
    } catch (err) {
      console.error('Error on init vehicle edit form ' + JSON.stringify(err));
    }
  }

  initResendEmailForm(): void {
    try {
      this.resendEmailForm = new FormGroup({
        emailAddress: new FormControl('', [Validators.required]),
        dateLastModified: new FormControl('', []),
        dateEmailResent: new FormControl('', []),
      });
    } catch (err) {}
  }

  getSpaceIdByEmailAddress(emailAddress: string): void {
    let spaceUserid: number;

    this.dataService.getSpaceUserIdForEmailAddress(emailAddress, 0, 0, this.parkerDetail.parkerId).subscribe(
      (res) => {
        spaceUserid = res;
      },
      (err: any) => {},
      () => {
        this.finishAddNewSpaceEmail(spaceUserid);
      },
    );
  }

  getFinalSpaceIdByEmailAddress(emailAddress: string): void {
    let spaceUserid: number;

    this.dataService.getSpaceUserIdForEmailAddress(emailAddress, 1, 0, this.parkerDetail.parkerId).subscribe(
      (res) => {
        spaceUserid = res;
      },
      (err: any) => {},
      () => {},
    );
  }

  getSpaceUserApnEmailResend(emailAddress: string): void {
    let spaceUserId: number;
    let userId: string = this._appGeneralState.userName;

    let dateResend: string = new Date().toLocaleString();

    this.dataService.getSpaceUserApnEmailResend(emailAddress, userId, dateResend).subscribe(
      (res) => {
        spaceUserId = res;
      },
      (err: any) => {
        this.snackBarService.open('This spACE User Account no longer exists.  Please contact spACE AR support.', '', {
          duration: 7000,
        });
      },
      () => {
        let action: any;

        this.resendEmailForm.patchValue({
          dateEmailResent: this.datePipe.transform(new Date(), 'MM/dd/yyyy'),
        });

        this.snackBarService.open('The request has been submitted to resend this email.', action, {
          duration: 3000,
        });
      },
    );
  }

  isEmailAvailableForParkerPaidSpaceAccount(emailAddress: string): void {
    let isAvailable: boolean;

    this.dataService
      .IsParkerPaidEmailAvailable(emailAddress)
      .pipe(takeUntilDestroyed(this.destroyRefService))
      .subscribe({
        next: (res) => {
          isAvailable = res;
        },
        error: (err: any) => {},
        complete: () => {
          if (!isAvailable) {
            //This email is already used in a company paid space account
            let action: any;
            this.snackBarService.open('This email address has already been used for a company paid account', action, {
              duration: 5000,
            });
          } else {
            this.getSpaceIdByEmailAddress(emailAddress);
          }
        },
      });
  }

  getSpaceEmailByParkerId(parkerId: string): void {
    let emailReturnData: ISpaceEmailByEntityId;

    this.dataService.getSpaceEmailAddressByParkerId(parkerId).subscribe(
      (res) => {
        emailReturnData = res;
      },
      (err: any) => {},
      () => {
        let lastDateEmailResendCheckDate: string = this.datePipe
          .transform(emailReturnData.lastDateEmailResend, 'MM/dd/yyyy')
          ?.toString()!;

        this.spaceEmailValue = emailReturnData.emailAddress;
        this.resendEmailMode = true;
        this.resendEmailForm.patchValue({
          emailAddress: emailReturnData.emailAddress,
          dateLastModified: this.datePipe.transform(emailReturnData.dateLastModified, 'MM/dd/yyyy'),
        });

        if (lastDateEmailResendCheckDate != '01/01/0001') {
          this.resendEmailForm.patchValue({
            dateEmailResent: this.datePipe.transform(emailReturnData.lastDateEmailResend, 'MM/dd/yyyy'),
          });
        }
      },
    );
  }

  postNewSpaceEmail(newSpaceEmailData: IApnNewSpaceEmailLink) {
    let newSpaceEmailId: number;

    this.dataService.postApnSpaceEmailLink(newSpaceEmailData).subscribe(
      (res) => {
        newSpaceEmailId = res;
      },
      (err: any) => {
        const errMessage: string = err.error;

        this.generalErrorAddingParkerToSpaceAccount('0ms', '0ms', errMessage);
      },
      () => {
        let snackBarMessage: string = '';

        //This has to be called after successfully adding the space account to initiate an email send
        this.getFinalSpaceIdByEmailAddress(newSpaceEmailData.EmailAddress);

        snackBarMessage = 'This email address has been saved as the spACE Email Address for this Parker Profile.';

        let action: any;
        this.snackBarService.open(snackBarMessage, action, {
          duration: 3000,
        });

        this.initResendEmailForm();
        this.resendEmailMode = true;
        this.resendEmailForm.patchValue({
          emailAddress: newSpaceEmailData.EmailAddress,
          dateLastModified: this.datePipe.transform(new Date(), 'MM/dd/yyyy'),
        });

        this.initEmailSpaceForm();
      },
    );
  }

  generalErrorAddingParkerToSpaceAccount(
    enterAnimationDuration: string,
    exitAnimationDuration: string,
    message: string,
  ): void {
    const dialogRef = this.dialog.open(ErrorDialogStopComponent, {
      data: { dlgTitle: 'Error Creating Space Account', dlgLine1: message, dlgLine2: '' },
      width: '450px',
      enterAnimationDuration,
      exitAnimationDuration,
    });

    dialogRef.afterClosed().subscribe((result) => {});
  }
}
